import React, { useContext, useEffect } from "react";
import { useForm, useFormState } from "react-final-form";
import { AuthContext, IntlContext } from "context";
import { Gender } from "types/user";
import { Category } from "types/category";
import { TextField, Radio, Checkbox, Textarea, Select } from "components/Form/Field";
import ReferenceField from "components/Form/Field/ReferenceField";
import { Role, TypeProviderUser } from "types/user";
import Address from "components/Form/Address";
import moment from "moment";
import { useReferences } from "hooks/reference";
import { COUNTRY_CODE_FRA } from "types/reference";

const ProfileFormFields: React.FC<{}> = () => {
    const intl = useContext(IntlContext);
    const { values, initialValues } = useFormState();
    const { account, currentRole } = useContext(AuthContext);
    const form = useForm();
    const reference = useReferences();
    interface CountryRef {
        code: string;
        code_iso: string;
        value: string;
    }

    const countries = reference.all('countries') as CountryRef[];
    const franceCountry = countries.filter((c) => c.code === COUNTRY_CODE_FRA).shift();

    if (franceCountry && (String(initialValues.country_birth_code) === franceCountry.code_iso) && initialValues.city_birth && initialValues.department_birth_code) {
        form.change('address_birth', `${initialValues.city_birth} ${initialValues.department_birth_code}`);
    }

    useEffect(() => {
        if (initialValues.country_birth_code) {
            if (franceCountry && (String(values.country_birth_code) === franceCountry.code_iso)) {
                form.change('is_without_france', '0');
            } else {
                form.change('is_without_france', '1');
            }
        }
    }, [form, franceCountry, initialValues.country_birth_code, values.country_birth_code]);

    useEffect(() => {
        if ((
            (values.is_without_france && values.is_without_france === '0') ||
            (initialValues.is_without_france && initialValues.is_without_france === '0')
        ) && franceCountry) {
            form.change('country_birth_code', franceCountry.code_iso);
        }

        if (
            (values.is_without_france && values.is_without_france === '1') ||
            (initialValues.is_without_france && initialValues.is_without_france === '1')
        ) {
            form.change('country_birth_code', initialValues.country_birth_code);
        }
    }, [form, franceCountry, initialValues.country_birth_code, initialValues.is_without_france, values.is_without_france]);

    return (
        <React.Fragment>
            <div className="register__group-fields">
                <div className="register__grid">
                    <div className="grid--2 grid--small-1 grid--has-gutter-3x register__grid">
                        <Radio
                            fieldControlProps={{
                                className: "field-control-radio field__row--inline",
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.gender"),
                            }}
                            inputProps={{
                                name: "gender",
                                required: true,
                                options: [
                                    {
                                        value: Gender.FEMALE,
                                        label: intl.formatMessage("generic.label.gender.FEMALE"),
                                    },
                                    {
                                        value: Gender.MALE,
                                        label: intl.formatMessage("generic.label.gender.MALE"),
                                    },
                                ],
                            }}
                        />
                    </div>

                    <div className="grid--2 grid--small-1 grid--has-gutter-3x register__grid">
                        <TextField
                            inputProps={{
                                name: "last_name",
                                required: true,
                                placeholder: intl.formatMessage("profile.label.last_name"),
                                maxLength: 255,
                                onBlur: () => {
                                    if (!values.birth_name) {
                                        form.change("birth_name", values.last_name);
                                    }
                                },
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.last_name"),
                            }}
                        />
                        <TextField
                            inputProps={{
                                name: "birth_name",
                                required: !account?.user.roles?.includes(Role.COMPANY),
                                placeholder: intl.formatMessage("profile.label.birth_name"),
                                maxLength: 255,
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.birth_name"),
                            }}
                        />
                        <TextField
                            inputProps={{
                                name: "first_name",
                                required: true,
                                placeholder: intl.formatMessage("profile.label.first_name"),
                                maxLength: 255,
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.first_name"),
                            }}
                        />
                        <TextField
                            inputProps={{
                                name: "phone",
                                required: false,
                                maxLength: 10,
                                placeholder: intl.formatMessage("profile.label.phone"),
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.phone"),
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="register__group-fields">
                <Address
                    inputProps={{
                        name: "address",
                        latitudeName: "coordinates_lat",
                        longitudeName: "coordinates_lon",
                        houseNumber: "house_number",
                        street: "street",
                        postcode: "postcode",
                        city: "city",
                        commune_code: "commune_code",
                        required: true,
                    }}
                    labelProps={{
                        label: account?.user.roles?.includes(Role.COMPANY)
                            ? intl.formatMessage("admin.user.label.company_address")
                            : intl.formatMessage("admin.user.label.address"),
                    }}
                />
            </div>

            <div className="register__group-fields">
                <div className="grid--2 grid--small-1 grid--has-gutter-3x register__grid">
                    <TextField
                        inputProps={{
                            name: "email",
                            required: true,
                            placeholder: intl.formatMessage("profile.label.email"),
                            maxLength: 255,
                        }}
                        labelProps={{
                            label: intl.formatMessage("profile.label.email"),
                        }}
                    />

                    {currentRole === Role.CLIENT && (
                        <TextField
                            inputProps={{
                                name: "resources.client.birthday",
                                type: "date",
                                max: moment().format("YYYY-MM-DD"),
                                required: true,
                                placeholder: intl.formatMessage("authentication.register.label.birthday"),
                                className: "react-date-picker--height-40 react-date-picker--type_date",
                            }}
                            labelProps={{
                                label: intl.formatMessage("authentication.register.label.birthday"),
                            }}
                        />
                    )}

                    {currentRole === Role.PROVIDER && (
                        <TextField
                            inputProps={{
                                name: "resources.provider.nir",
                                required: false,
                                maxLength: 15,
                                placeholder: intl.formatMessage("profile.label.nir"),
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.nir"),
                                className: "field__label profile--provider_mandatory_field"
                            }}
                        />
                    )}

                    <Radio
                        fieldControlProps={{
                            className: "field-control-radio field__row--inline",
                        }}
                        labelProps={{
                            label: intl.formatMessage("register.label.birth.is_french"),
                        }}
                        inputProps={{
                            name: "is_without_france",
                            required: false,
                            options: [
                                {
                                    value: "1",
                                    label: intl.formatMessage("generic.label.yes"),
                                },
                                {
                                    value: "0",
                                    label: intl.formatMessage("generic.label.no"),
                                },
                            ],
                            onChange: (e) => {
                                form.change('is_without_france', e.target.value);
                                if (e.target.value === '1') {
                                    form.change('city_birth', null);
                                    form.change('commune_code_birth', null);
                                    form.change('department_birth_code', null);
                                }
                            }
                        }}
                    />

                    {values["is_without_france"] === "0" && (
                        <Address
                            inputProps={{
                                name: "address_birth",
                                type: "municipality",
                                latitudeName: "coordinates_lat",
                                longitudeName: "coordinates_lon",
                                city: "city_birth",
                                commune_code: "commune_code_birth",
                                department_code: "department_birth_code",
                                required: false,
                            }}
                            labelProps={{
                                label: intl.formatMessage("register.label.city.birth"),
                                className: currentRole === Role.PROVIDER ? "field__label profile--provider_mandatory_field" : "field__label"
                            }}
                        />
                    )}

                    {values["is_without_france"] === "1" && (
                        <>
                            <Select
                                inputProps={{
                                    name: "country_birth_code",
                                    placeholder: intl.formatMessage("register.label.country.birth"),
                                    options: countries
                                        .filter((c) => c.code !== COUNTRY_CODE_FRA)
                                        .map((c, i: number) => {
                                            return {
                                                label: `${c.value.charAt(0).toUpperCase()}${c.value.slice(1).toLowerCase()}`,
                                                value: c.code_iso,
                                                key: i
                                            };
                                        }).sort((a, b) => a.label.localeCompare(b.label)),
                                    required: true,
                                }}
                                labelProps={{
                                    label: intl.formatMessage("register.label.country.birth"),
                                    className: currentRole === Role.PROVIDER ? "field__label profile--provider_mandatory_field" : "field__label"
                                }}
                            />
                            <TextField
                                inputProps={{
                                    name: "city_birth",
                                    required: false,
                                    placeholder: intl.formatMessage("register.label.city.birth"),
                                }}
                                labelProps={{
                                    label: intl.formatMessage("register.label.city.birth"),
                                }}
                            />
                        </>
                    )}

                    {currentRole === Role.PROVIDER && (
                        <TextField
                            inputProps={{
                                name: "resources.provider.birthday",
                                type: "date",
                                required: false,
                                max: moment().format("YYYY-MM-DD"),
                                placeholder: intl.formatMessage("authentication.register.label.birthday"),
                                className: "react-date-picker--height-40 react-date-picker--type_date",
                            }}
                            labelProps={{
                                label: intl.formatMessage("authentication.register.label.birthday"),
                                className: "field__label profile--provider_mandatory_field"
                            }}
                        />
                    )}
                </div>
            </div>

            {currentRole === Role.CLIENT && (
                <div className="register__group-fields">
                    <div className="grid--2 grid--small-1 grid--has-gutter-3x register__grid">
                        <Radio
                            fieldControlProps={{
                                className: "field-control-radio field__row--inline",
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.has_pet"),
                            }}
                            inputProps={{
                                name: "resources.client.has_pet",
                                options: [
                                    {
                                        value: "1",
                                        label: intl.formatMessage("generic.label.yes"),
                                    },
                                    {
                                        value: "0",
                                        label: intl.formatMessage("generic.label.no"),
                                    },
                                ]
                            }}
                        />
                        {values.resources.client.has_pet === "1" && (
                            <ReferenceField
                                refType="pet_type"
                                inputProps={{
                                    name: "resources.client.pet_type",
                                    multiple: true
                                }}
                                labelProps={{
                                    label: intl.formatMessage("authentication.register.label.has_pet.pet_type"),
                                }}
                            />
                        )}
                    </div>
                    <div className="grid--1 grid--small-1 grid--has-gutter-3x register__grid">
                        <Checkbox
                            labelProps={{
                                label: intl.formatMessage("profile.label.services"),
                            }}
                            fieldControlProps={{
                                className: "services__group",
                            }}
                            inputProps={{
                                name: "resources.client.services",
                                className: "services__input-checkbox",
                                groupClassName: "services__input-group",
                                labelClassName: "services__input-label",
                                options: [
                                    {
                                        value: Category.HOUSEKEEPING.toString(),
                                        label: intl.formatMessage("category.housekeeping"),
                                    },
                                    {
                                        value: Category.ASSISTANCE.toString(),
                                        label: intl.formatMessage("category.assistance"),
                                    },
                                    {
                                        value: Category.GARDENING.toString(),
                                        label: intl.formatMessage("category.gardening"),
                                    },
                                    {
                                        value: Category.HANDIWORK.toString(),
                                        label: intl.formatMessage("category.handiwork"),
                                    },
                                    {
                                        value: Category.PRIVATE_TUTORING.toString(),
                                        label: intl.formatMessage("category.private_tutoring"),
                                    },
                                    {
                                        value: Category.CHILDCARE.toString(),
                                        label: intl.formatMessage("category.childcare"),
                                    },
                                ],
                            }}
                        />
                        {/*<Radio
                            fieldControlProps={{
                                className: "field-control-radio field__row--inline",
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.is_attach_company"),
                            }}
                            inputProps={{
                                name: "is_attach_company",
                                required: true,
                                options: [
                                    {
                                        value: "1",
                                        label: intl.formatMessage("generic.label.yes"),
                                    },
                                    {
                                        value: "0",
                                        label: intl.formatMessage("generic.label.no"),
                                    },
                                ],
                            }}
                        />*/}
                    </div>
                    {/*values.is_attach_company === "1" && (
                        <div className="grid--2 grid--small-1 grid--has-gutter-3x register__grid">
                            <Autocomplete
                                inputProps={{
                                    name: "resources.client.company_id",
                                    required: true,
                                    keySearch: "filter_name",
                                    dataValue: "id",
                                    dataLabel: formatCompanyLabel,
                                    dataFetcher: fetchAutocompleteCompany,
                                    value: values.resources.client.company_id,
                                    valueKeySearch: "filter_id",
                                }}
                                labelProps={{
                                    label: intl.formatMessage("profile.label.name_company"),
                                }}
                            />

                            <TextField
                                inputProps={{
                                    name: "resources.client.code",
                                    required: true,
                                    placeholder: intl.formatMessage("profile.label.code_company"),
                                }}
                                labelProps={{
                                    label: intl.formatMessage("profile.label.code_company"),
                                }}
                            />
                        </div>
                    )*/}
                    <div className="grid--1 grid--small-1 grid--has-gutter-3x register__grid">
                        <Textarea
                            inputProps={{
                                name: "resources.client.comment",
                                placeholder: "Commentaires",
                            }}
                            labelProps={{
                                label: intl.formatMessage("profile.label.other_informations"),
                            }}
                        />
                    </div>
                </div>
            )}

            {currentRole === Role.PROVIDER && (
                <div className="register__group-fields">
                    <div className="grid--2 grid--small-1 grid--has-gutter-3x register__grid">
                        <Select
                            fieldControlProps={{
                                className: "select-wrapper",
                            }}
                            labelProps={{
                                label: intl.formatMessage("authentication.register.label.csp"),
                            }}
                            inputProps={{
                                name: "resources.provider.csp",
                                options: [
                                    {
                                        value: TypeProviderUser.STUDENT,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.STUDENT}`
                                        ),
                                    },
                                    {
                                        value: TypeProviderUser.EMPLOYEE,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.EMPLOYEE}`
                                        ),
                                    },
                                    {
                                        value: TypeProviderUser.SEEKING,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.SEEKING}`
                                        ),
                                    },
                                    {
                                        value: TypeProviderUser.RETIRED,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.RETIRED}`
                                        ),
                                    },
                                    {
                                        value: TypeProviderUser.FREELANCE,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.FREELANCE}`
                                        ),
                                    },
                                    {
                                        value: TypeProviderUser.TEACHER,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.TEACHER}`
                                        ),
                                    },
                                    {
                                        value: TypeProviderUser.OTHER,
                                        label: intl.formatMessage(
                                            `authentication.register.label.csp.${TypeProviderUser.OTHER}`
                                        ),
                                    },
                                ],
                            }}
                        />
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default ProfileFormFields;
